import React, {useEffect, useState} from 'react';
import {
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    IconButton,
    MessageBar,
    MessageBarType,
    Modal,
    PrimaryButton,
    Separator,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    TextField,
    Toggle,
    getTheme
} from '@fluentui/react';
import {toast} from 'react-toastify';
import useCan, {STOREFRONT_TEMPLATES_GLOBAL} from '../../../permissions/ability';
import {
    useDeleteStorefrontTemplateMutation,
    useGetStorefrontTemplateQuery,
    useUpdateStorefrontTemplateMutation
} from '../../../store/endpoints/StorefrontTemplateEndpoints';
import TemplateGalleryEditor from './TemplateGalleryEditor';
import TemplateVisibilityFields, {ITemplateVisibilityValue} from './TemplateVisibilityFields';

interface IEditStorefrontTemplateModalProps {
    isOpen: boolean;
    onDismiss: () => void;
    templateId: string;
    // Fired after the template is removed, so callers can drop whatever pointed at it.
    onDeleted?: () => void;
}

// Edit an existing storefront template's metadata (name, description, disabled, global) and manage
// its preview gallery. The PUT carries the whole template view model; the API honors
// name/description/disabled/isSystem/gallery.
const EditStorefrontTemplateModal: React.FC<IEditStorefrontTemplateModalProps> = (props) => {
    const {isOpen, onDismiss, templateId, onDeleted} = props;

    const theme = getTheme();
    const canGlobal = useCan(STOREFRONT_TEMPLATES_GLOBAL);

    const {data: template, isLoading, isFetching} = useGetStorefrontTemplateQuery(templateId, {
        skip: !isOpen || !templateId
    });
    const [updateTemplate] = useUpdateStorefrontTemplateMutation();
    const [deleteTemplate] = useDeleteStorefrontTemplateMutation();

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [longDescription, setLongDescription] = useState('');
    const [disabled, setDisabled] = useState(false);
    const [visibility, setVisibility] = useState<ITemplateVisibilityValue>({
        isSystem: false,
        organizations: [],
        productLevel: [],
        entitlements: []
    });
    const [saving, setSaving] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [error, setError] = useState<string | undefined>();

    useEffect(() => {
        if (template) {
            setName(template.name ?? '');
            setDescription(template.description ?? '');
            setLongDescription(template.longDescription ?? '');
            setDisabled(!!template.disabled);
            setVisibility({
                isSystem: !!template.isSystem,
                organizations: template.organizations ?? [],
                productLevel: template.productLevel ?? [],
                entitlements: template.entitlements ?? []
            });
            setError(undefined);
        }
    }, [template]);

    const onSave = async () => {
        if (!template) return;
        if (!name.trim()) {
            setError('Template name is required');
            return;
        }
        setSaving(true);
        setError(undefined);
        try {
            const res = await updateTemplate({
                id: templateId,
                template: {
                    ...template,
                    name: name.trim(),
                    description: description.trim() || undefined,
                    longDescription: longDescription.trim() || undefined,
                    disabled,
                    isSystem: canGlobal ? visibility.isSystem : template.isSystem,
                    organizations: canGlobal ? visibility.organizations : template.organizations,
                    productLevel: canGlobal ? visibility.productLevel : template.productLevel,
                    entitlements: canGlobal ? visibility.entitlements : template.entitlements
                }
            }).unwrap();
            if (res.success) {
                toast.success('Template updated');
                onDismiss();
            } else {
                setError(res.message || 'Failed to update template');
                toast.error(res.message || 'Failed to update template');
            }
        } catch {
            setError('Failed to update template');
            toast.error('Failed to update template');
        }
        setSaving(false);
    };

    const onDelete = async () => {
        setConfirmDelete(false);
        const templateName = template?.name || 'template';
        setSaving(true);
        const toastId = toast.loading(`Removing ${templateName}...`);
        try {
            const res = await deleteTemplate(templateId).unwrap();
            if (res.success) {
                toast.update(toastId, {
                    type: 'success',
                    render: `${templateName} removed`,
                    isLoading: false,
                    autoClose: 3000
                });
                onDeleted?.();
                onDismiss();
            } else {
                toast.update(toastId, {
                    type: 'error',
                    render: res.message || `Failed to remove ${templateName}`,
                    isLoading: false,
                    autoClose: 4000
                });
                setError(res.message || 'Failed to remove template');
            }
        } catch {
            toast.update(toastId, {
                type: 'error',
                render: `Failed to remove ${templateName}`,
                isLoading: false,
                autoClose: 4000
            });
            setError('Failed to remove template');
        }
        setSaving(false);
    };

    return (
        <>
        <Modal isOpen={isOpen} onDismiss={onDismiss} isBlocking={true}>
            <Stack tokens={{childrenGap: 12}} style={{padding: 24, width: 560, maxWidth: '90vw'}}>
                <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                    <Text variant="xLarge">Edit Template</Text>
                    <IconButton iconProps={{iconName: 'Cancel'}} ariaLabel="Close" onClick={onDismiss}/>
                </Stack>

                {(isLoading || isFetching) && !template ? (
                    <Spinner size={SpinnerSize.large} label="Loading..."/>
                ) : !template ? (
                    <MessageBar messageBarType={MessageBarType.error}>Template not found.</MessageBar>
                ) : (
                    <Stack tokens={{childrenGap: 12}}>
                        <TextField
                            label="Template Name"
                            required
                            value={name}
                            disabled={saving}
                            onChange={(_, v) => setName(v ?? '')}
                        />
                        <TextField
                            label="Description"
                            multiline
                            rows={3}
                            value={description}
                            disabled={saving}
                            onChange={(_, v) => setDescription(v ?? '')}
                        />
                        <TextField
                            label="Detailed Description"
                            description="Markdown supported. Shown on the template's details page."
                            multiline
                            rows={8}
                            value={longDescription}
                            disabled={saving}
                            onChange={(_, v) => setLongDescription(v ?? '')}
                        />
                        <Toggle
                            label={(
                                <Stack>
                                    <Text>Disabled</Text>
                                    <Text variant="small">Hidden from the template picker</Text>
                                </Stack>
                            )}
                            checked={disabled}
                            disabled={saving}
                            onText="On"
                            offText="Off"
                            onChange={(_, checked) => setDisabled(!!checked)}
                        />
                        {canGlobal && (
                            <TemplateVisibilityFields
                                value={visibility}
                                disabled={saving}
                                onChange={(partial) => setVisibility(prev => ({...prev, ...partial}))}
                            />
                        )}

                        <Separator/>
                        <TemplateGalleryEditor templateId={templateId} showReorder={true}/>

                        {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
                        <Stack horizontal horizontalAlign="space-between" verticalAlign="center" tokens={{childrenGap: 8}}>
                            <DefaultButton
                                text="Remove Template"
                                iconProps={{iconName: 'Delete'}}
                                onClick={() => setConfirmDelete(true)}
                                disabled={saving}
                                styles={{
                                    root: {color: theme.palette.red, borderColor: theme.palette.red},
                                    rootHovered: {color: theme.palette.redDark, borderColor: theme.palette.redDark},
                                    iconHovered: {color: theme.palette.redDark},
                                    icon: {color: theme.palette.red}
                                }}
                            />
                            <Stack horizontal tokens={{childrenGap: 8}}>
                                <DefaultButton text="Cancel" onClick={onDismiss} disabled={saving}/>
                                <PrimaryButton text="Save" onClick={onSave} disabled={saving}/>
                            </Stack>
                        </Stack>
                    </Stack>
                )}
            </Stack>
        </Modal>
        <Dialog
            hidden={!confirmDelete}
            onDismiss={() => setConfirmDelete(false)}
            dialogContentProps={{
                type: DialogType.normal,
                title: 'Remove Template',
                subText: 'This deletes the template. Storefronts already created from it are not affected.',
            }}
        >
            <DialogFooter>
                <PrimaryButton text="Remove" onClick={onDelete}/>
                <DefaultButton text="Cancel" onClick={() => setConfirmDelete(false)}/>
            </DialogFooter>
        </Dialog>
        </>
    );
};

export default EditStorefrontTemplateModal;
